// The single place that talks to the core.
//
// Authentication is a cookie, so nothing here carries a token. What it does
// carry is the CSRF token, which the server sets as a readable cookie and
// expects echoed back on anything that changes state. The portal's pair is
// named separately from the administration interface's, so that somebody who
// is both an administrator and an employee can be signed in to both at once.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly fields?: Record<string, string>,
    readonly body?: unknown,
  ) {
    super(detail);
    this.name = "ApiError";
  }

  get isUnauthenticated() {
    return this.status === 401;
  }

  /** The announcements a clash named, when there was one. */
  get conflicts(): Card[] | undefined {
    if (this.status !== 409) return undefined;
    const b = this.body as { conflicts?: Card[] } | undefined;
    return b?.conflicts;
  }
}

/**
 * The CSRF token belonging to whichever session this browser is using.
 *
 * Two names, because there are two ways to be in the portal: as yourself, and
 * as somebody an administrator is looking at on your behalf. The impersonation
 * pair wins when it is there, matching the server's own precedence — sending
 * the wrong one back would fail every write with a refusal that reads like a
 * stale tab.
 */
function csrfToken(): string {
  for (const name of ["mbm_impersonate_csrf", "mbm_portal_csrf"]) {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
    if (match?.[1]) return decodeURIComponent(match[1]);
  }
  return "";
}

type Options = { method?: string; body?: unknown; signal?: AbortSignal };

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = {};

  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (method !== "GET" && method !== "HEAD") headers["X-CSRF-Token"] = csrfToken();

  const res = await fetch(path, {
    method,
    headers,
    credentials: "same-origin",
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const parsed: unknown = text ? safeParse(text) : undefined;

  if (!res.ok) {
    const problem = parsed as
      | { detail?: string; fields?: Record<string, string> }
      | undefined;
    throw new ApiError(
      res.status,
      problem?.detail ?? res.statusText,
      problem?.fields,
      parsed,
    );
  }
  return parsed as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// --- what the server sends ---------------------------------------------------
//
// These mirror internal/app/portal. The descriptor types are the important
// ones: they are the instruction for what to draw, which is what replaces the
// five booleans the legacy interface had to interpret for itself.

export type Discovery = {
  method: "password" | "oidc";
  branding?: { companyName?: string; accentColor?: string; logoUrl?: string };
  provider?: { displayName: string; start: string };
  canResetPassword?: boolean;
};

export type DateField = {
  required: boolean;
  /** "day" marks a date announcement: the date is what the caller hears. The
   *  clock still lets a time be chosen, as the legacy interface did. */
  granularity: "minute" | "day";
  allowOpenEnded?: boolean;
  labelKey: string;
  defaultKey?: string;
};

export type ProxyOption = {
  id: number;
  name: string;
  email: string;
  selected?: boolean;
  /** False when the announcement cannot name this person today — in the
   *  recorded world, because their name and number were never recorded. */
  canBeNamed: boolean;
};

export type ProxyField = {
  required: boolean;
  source: "users" | "secretariats";
  labelKey: string;
  options: ProxyOption[];
};

export type NotifyField = {
  show: boolean;
  channel: string;
  default: string;
  options: { value: string; labelKey: string }[];
};

export type RepeatOption = {
  value: string;
  maxDurationHours?: number;
  maxDurationDays?: number;
};

export type FormDescriptor = {
  companyMessageId: number;
  name: string;
  subtitle?: string;
  body?: string;
  icon: string;
  summaryTitleKey: string;
  kind: "absence" | "timer";
  fields: string[];
  start?: DateField;
  end?: DateField;
  proxy?: ProxyField;
  notify?: NotifyField;
  repeat?: { options: RepeatOption[] };
  displayName?: { required: boolean; maxLength: number };
  preview: boolean;
  canPlan: boolean;
  blockedReasonKey?: string;
};

export type Card = {
  id: number;
  companyMessageId: number;
  name: string;
  icon: string;
  startsAt: string;
  endsAt?: string;
  openEnded: boolean;
  rangeText: string;
  durationText: string;
  proxy?: { id: number; name: string; email: string };
  notify: string;
  repeat: string;
  displayName?: string;
  state: string;
  statusKey: string;
  statusTone: "neutral" | "info" | "success" | "warning" | "danger";
  errorStage: number;
  canCancel: boolean;
  canEndNow: boolean;
  canRetry: boolean;
};

export type FavouriteCard = {
  slot: number;
  empty: boolean;
  companyMessageId?: number;
  name?: string;
  icon?: string;
  displayName?: string;
  durationMinutes?: number;
  durationText?: string;
  openEnded?: boolean;
  proxy?: { id: number; name: string; email: string };
  notify?: string;
  available: boolean;
  unavailableReasonKey?: string;
};

export type Bootstrap = {
  me: {
    userId: number;
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
    gender?: string;
    phone?: string;
    vpn?: string;
    department?: string;
    hasMailboxPin: boolean;
    recording: string;
    recordingNoteKey?: string;
    notifyDefault: string;
    /** The legacy defaultProxy and defaultVirtualProxy. */
    favouriteProxy?: { id: number; name: string; email: string };
    favouriteSecretariat?: { id: number; name: string; email: string };
    canAdminister: boolean;
    /** Set only when an administrator is using the portal in this person's name. */
    impersonatedBy?: { name: string; email: string };
  };
  company: {
    id: number;
    name: string;
    notifyChannel: string;
    branding: { accentColor?: string; logoUrl?: string; backgroundUrl?: string };
  };
  messages: FormDescriptor[];
  announcements: Card[];
  favourites: FavouriteCard[];
  limits: { favourites: number };
  serverTime: string;
  timezone: string;
  poll: { intervalMs: number; fastIntervalMs: number };
};

/** What the settings screen offers for the two favourites. */
export type ProxyLists = {
  users: ProxyOption[];
  secretariats: ProxyOption[];
};

export type PreviewResult = {
  url: string;
  durationSeconds: number;
};

export type TokenInfo = {
  purpose: "invite" | "reset";
  email: string;
  policyHint?: string;
  expiresAt: string;
};
